import {
  BillingCycle,
  ComputedSubscriptionStatus,
  PaymentDueType,
  PaymentMonitoringAmountField,
  PaymentMonitoringItem,
  PaymentMonitoringSummary,
} from '../types/subscription.type';
import { OrganizationServiceStatus } from '../types/organization.type';
import { resolvePaymentMonitoringServiceStatus } from './organizationServiceStatus.utils';

export interface PaymentMonitoringFilters {
  search: string;
  dueInMonth: string;
  overdueOnly: boolean;
  tagId: string;
  amountMin: string;
  amountMax: string;
  amountField: PaymentMonitoringAmountField;
  dueType: string;
  planCode: string;
  billingCycle: string;
  subscriptionStatus: string;
  serviceStatus: string;
}

function parseAmount(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const num = parseFloat(value);
  return Number.isNaN(num) ? null : num;
}

function itemServiceStatus(
  item: PaymentMonitoringItem,
  orgServiceStatusById?: ReadonlyMap<string, OrganizationServiceStatus>,
): OrganizationServiceStatus | undefined {
  return resolvePaymentMonitoringServiceStatus(item, orgServiceStatusById);
}

export function filterPaymentMonitoringItems(
  items: PaymentMonitoringItem[],
  filters: PaymentMonitoringFilters,
  orgServiceStatusById?: ReadonlyMap<string, OrganizationServiceStatus>,
): PaymentMonitoringItem[] {
  let result = items;

  const query = filters.search.trim().toLowerCase();
  if (query) {
    result = result.filter((item) => {
      const name = item.organization_name?.toLowerCase() ?? '';
      const email = item.organization_email?.toLowerCase() ?? '';
      return name.includes(query) || email.includes(query);
    });
  }

  if (filters.overdueOnly) {
    result = result.filter((item) => item.is_overdue);
  }

  if (filters.tagId) {
    result = result.filter((item) =>
      (item.follow_up_tags ?? []).some((tag) => tag.id === filters.tagId),
    );
  }

  if (filters.dueInMonth) {
    result = result.filter((item) => {
      if (item.is_overdue) return true;
      if (!item.due_date) return false;
      return item.due_date.slice(0, 7) === filters.dueInMonth;
    });
    result = result.filter((item) => item.payment_due_type !== 'none');
  }

  if (filters.amountMin || filters.amountMax) {
    const field = filters.amountField;
    const min = parseAmount(filters.amountMin);
    const max = parseAmount(filters.amountMax);
    result = result.filter((item) => {
      const raw = field === 'remaining_total_with_vat'
        ? item.remaining_total_with_vat
        : item.amount_due_now;
      const amount = parseAmount(raw);
      if (amount == null) return false;
      if (min != null && amount < min) return false;
      if (max != null && amount > max) return false;
      return true;
    });
  }

  if (filters.dueType) {
    result = result.filter((item) => item.payment_due_type === filters.dueType);
  }

  if (filters.planCode) {
    result = result.filter((item) => item.plan_code === filters.planCode);
  }

  if (filters.billingCycle) {
    result = result.filter((item) => item.billing_cycle === filters.billingCycle);
  }

  if (filters.subscriptionStatus) {
    result = result.filter(
      (item) => item.subscription_status === filters.subscriptionStatus,
    );
  }

  if (filters.serviceStatus) {
    result = result.filter(
      (item) => itemServiceStatus(item, orgServiceStatusById) === filters.serviceStatus,
    );
  }

  return result;
}

export function computePaymentMonitoringSummary(
  items: PaymentMonitoringItem[],
): PaymentMonitoringSummary {
  const withDue = items.filter((item) => item.payment_due_type !== 'none');
  const overdue = items.filter((item) => item.is_overdue);
  const totalDue = withDue.reduce(
    (sum, item) => sum + (parseAmount(item.amount_due_now) ?? 0),
    0,
  );
  const totalRemaining = withDue.reduce(
    (sum, item) => sum + (parseAmount(item.remaining_total_with_vat) ?? 0),
    0,
  );

  return {
    total_organizations: items.length,
    with_payment_due: withDue.length,
    overdue_count: overdue.length,
    total_amount_due: withDue.length > 0 ? totalDue.toFixed(2) : null,
    total_remaining_with_vat: withDue.length > 0 ? totalRemaining.toFixed(2) : null,
  };
}

export function hasActivePaymentMonitoringFilters(filters: PaymentMonitoringFilters): boolean {
  return Boolean(
    filters.search.trim()
    || filters.dueInMonth
    || filters.overdueOnly
    || filters.tagId
    || filters.amountMin
    || filters.amountMax
    || filters.dueType
    || filters.planCode
    || filters.billingCycle
    || filters.subscriptionStatus
    || filters.serviceStatus,
  );
}

export const EMPTY_PAYMENT_MONITORING_FILTERS: PaymentMonitoringFilters = {
  search: '',
  dueInMonth: '',
  overdueOnly: false,
  tagId: '',
  amountMin: '',
  amountMax: '',
  amountField: 'amount_due_now',
  dueType: '',
  planCode: '',
  billingCycle: '',
  subscriptionStatus: '',
  serviceStatus: '',
};

export type { PaymentDueType, BillingCycle, ComputedSubscriptionStatus };
